import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { forgotPassword, confirmForgotPassword } from '../auth/cognitoAuth';
import Footer from '../components/Footer';

/**
 * Self-service reset - unlike SetNewPassword.jsx (an admin-forced reset
 * mid sign-in that has to carry a live CognitoUser object through router
 * state), this flow is stateless: both Cognito calls only need the email
 * string, so both steps live on this one page as plain component state
 * rather than a second route.
 */
export default function ForgotPassword() {
  const navigate = useNavigate();
  const [step, setStep] = useState('request'); // 'request' | 'confirm'
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [destination, setDestination] = useState(null);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  async function handleRequestCode(e) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const result = await forgotPassword(email);
      setDestination(result.destination || null);
      setStep('confirm');
    } catch (err) {
      setError(err.message || 'Could not start password reset.');
    } finally {
      setSubmitting(false);
    }
  }

  async function handleConfirm(e) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await confirmForgotPassword(email, code, newPassword);
      navigate('/login', { state: { passwordResetSuccess: true } });
    } catch (err) {
      setError(err.message || 'Could not reset password.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="auth-page d-flex flex-column min-vh-100">
      <div className="flex-grow-1 d-flex align-items-center justify-content-center">
        <div className="card auth-card shadow-sm border-0">
          <div className="auth-accent-bar" />
          <div className="card-body">
            <div className="text-center mb-4">
              <img src="/bizmo-logo.png" alt="Bizmoto" height="44" />
            </div>

            {step === 'request' && (
              <form onSubmit={handleRequestCode}>
                <h1 className="h5 mb-2 text-center">Reset your password</h1>
                <p className="text-muted small text-center mb-3">
                  Enter your account email and we&rsquo;ll send a verification code.
                </p>
                <div className="mb-3">
                  <label className="form-label small">Email</label>
                  <input
                    type="email"
                    className="form-control"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    autoComplete="username"
                  />
                </div>
                {error && <p className="text-danger small">{error}</p>}
                <button type="submit" className="btn btn-primary w-100" disabled={submitting}>
                  {submitting ? 'Sending…' : 'Send verification code'}
                </button>
                <p className="text-center small mt-3 mb-0">
                  <Link to="/login">Back to sign in</Link>
                </p>
              </form>
            )}

            {step === 'confirm' && (
              <form onSubmit={handleConfirm}>
                <h1 className="h5 mb-2 text-center">Enter your new password</h1>
                <p className="text-muted small text-center mb-3">
                  {destination
                    ? `We sent a verification code to ${destination}.`
                    : 'Enter the verification code we sent you.'}
                </p>
                <div className="mb-3">
                  <label className="form-label small">Verification code</label>
                  <input
                    type="text"
                    className="form-control"
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    required
                    autoComplete="one-time-code"
                    inputMode="numeric"
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label small">New password</label>
                  <input
                    type="password"
                    className="form-control"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                    minLength={12}
                    autoComplete="new-password"
                  />
                </div>
                {error && <p className="text-danger small">{error}</p>}
                <button type="submit" className="btn btn-primary w-100" disabled={submitting}>
                  {submitting ? 'Resetting…' : 'Reset password'}
                </button>
                <p className="text-center small mt-3 mb-0">
                  <button
                    type="button"
                    className="btn btn-link btn-sm p-0"
                    onClick={() => setStep('request')}
                  >
                    Use a different email
                  </button>
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
